import React from 'react';

/**
 * StatusPill Component
 * 
 * Displays a status indicator with appropriate styling based on the status value.
 * 
 * @param {Object} props
 * @param {string} props.status - The current status (e.g., 'Done', 'Started', 'Abandoned', etc.)
 * @param {string} props.className - Additional CSS classes to apply to the component
 * @returns {JSX.Element} A styled status indicator
 */
function StatusPill({ status, className = '' }) {
  // Determine styling and display text based on status
  const getStatusInfo = () => {
    // Handle challenge status codes
    if (status.startsWith('cs_')) {
      switch (status) {
        case 'cs_done':
          return { 
            style: 'text-white border',
            inlineStyle: { 
              backgroundColor: '#33C6B6',
              borderColor: '#A9EED1'
            },
            text: 'Done'
          };
        case 'cs_started':
          return { 
            style: 'text-white border',
            inlineStyle: { 
              backgroundColor: '#33C6B6',
              borderColor: '#A9EED1'
            },
            text: 'Started'
          };
        case 'cs_abandoned':
          return { 
            style: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
            text: 'Abandoned'
          };
        case 'cs_submitted':
          return { 
            style: 'bg-purple-100 text-purple-800 border border-purple-300',
            text: 'Submitted'
          };
        case 'cs_assessing':
          return { 
            style: 'bg-indigo-100 text-indigo-800 border border-indigo-300',
            text: 'Assessing'
          };
        case 'cs_not_started':
          return { 
            style: 'bg-gray-100 text-gray-800 border border-gray-300',
            text: 'Not Started'
          };
        default:
          return { 
            style: 'bg-gray-100 text-gray-800 border border-gray-300',
            text: status
          };
      }
    }
    // Handle questionnaire status codes
    else if (status.startsWith('qs_')) {
      switch (status) {
        case 'qs_done':
          return { 
            style: 'text-white border',
            inlineStyle: { 
              backgroundColor: '#33C6B6',
              borderColor: '#A9EED1'
            },
            text: 'Done'
          };
        case 'qs_started':
          return { 
            style: 'text-white border',
            inlineStyle: { 
              backgroundColor: '#33C6B6',
              borderColor: '#A9EED1'
            },
            text: 'Started'
          };
        case 'qs_abandoned':
          return { 
            style: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
            text: 'Abandoned'
          };
        case 'qs_not_started':
          return { 
            style: 'bg-gray-100 text-gray-800 border border-gray-300',
            text: 'Not Started'
          };
        default:
          return { 
            style: 'bg-gray-100 text-gray-800 border border-gray-300',
            text: status
          };
      }
    }
    // Handle legacy or non-prefixed status codes
    else {
      switch (status) {
        case 'Done':
          return { 
            style: 'bg-green-100 text-green-800 border border-green-300',
            text: 'Done'
          };
        case 'Started':
          return { 
            style: 'text-white border',
            inlineStyle: { 
              backgroundColor: '#33C6B6',
              borderColor: '#A9EED1'
            },
            text: 'Started'
          };
        case 'Abandoned':
          return { 
            style: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
            text: 'Abandoned'
          };
        default:
          return { 
            style: 'bg-gray-100 text-gray-800 border border-gray-300',
            text: status
          };
      }
    }
  };

  const { style, text, inlineStyle } = getStatusInfo();

  return (
    <span 
      className={`px-4 py-1.5 rounded-full text-sm font-medium shadow-sm ${style} ${className}`}
      style={inlineStyle}
    >
      {text}
    </span>
  );
}

export default StatusPill;
